import { ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Circle, Eye, Layers, Pencil, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { Space } from "../models/space.model";
import { SpaceDetails } from './space-details.component';
import { AuthContext } from '../../../../providers/AuthProvider';

/**
 * An action button that sits on top of a room photo.
 *
 * Four of these ride over the thumbnail, and a photo is an unpredictable
 * background, so they carry their own translucent ground instead of the
 * transparent one a ghost button would have. The tooltip lives inside the
 * component so the `asChild` child is always a real Button — a plain function
 * component there would lose its ref on React 18 and the tooltip would mount
 * off-screen.
 */
const OverlayAction = ({
    label,
    ariaLabel,
    onClick,
    disabled,
    active,
    className,
    children,
}: {
    label?: string;
    /** Accessible name. Falls back to `label`; passed explicitly by the actions that carry no tooltip. */
    ariaLabel?: string;
    onClick: () => void;
    disabled?: boolean;
    active?: boolean;
    className?: string;
    children: ReactNode;
}) => {
    const button = (
        <Button
            variant="outline"
            size="icon-sm"
            disabled={disabled}
            onClick={onClick}
            aria-label={ariaLabel ?? label}
            className={cn(
                'border-border/60 bg-background/85 backdrop-blur-sm',
                active && 'border-foreground bg-foreground text-background hover:bg-foreground hover:text-background',
                className,
            )}
        >
            {children}
        </Button>
    );

    // The admin edit/delete buttons never had a tooltip; only label the ones that did.
    if (!label) return button;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                {/* A disabled button emits no pointer events, so the span carries the tooltip. */}
                <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
        </Tooltip>
    );
};

export const SpacesAsGrid = (props) => {
    const [t] = useTranslation();
    const [selectedSpaceForDetails, setSelectedSpaceForDetails] = useState(undefined);
    const { isSuperAdmin } = useContext(AuthContext);
    // readOnly: render the spaces purely as a preview — no eye, select,
    // edit or delete icons. Used by the super-admin "Selected Spaces"
    // card on the vendor edit page.
    const readOnly: boolean = !!props.readOnly;
    const showAdminEdit = isSuperAdmin && !readOnly && typeof props.handleEditSpace === 'function';
    const showAdminDelete = isSuperAdmin && !readOnly && typeof props.onDelete === 'function';
    const showOpenInManager = isSuperAdmin && !readOnly && typeof props.onOpenInManager === 'function';

    const toggleSelect = (space) => {
        // Check if the space is already selected by looking at IDs
        const isSpaceSelected = props.selectedSpaces.some(item => item._id === space._id);

        if (isSpaceSelected) {
            // Remove the space from selection
            props.setSelectedSpaces(props.selectedSpaces.filter(item => item._id !== space._id));
        } else {
            // Only add if we haven't reached the limit (excluding visitor-uploaded spaces from count)
            const nonVisitorSpacesCount = props.selectedSpaces.filter(s => !s.uploadedByVisitor).length;
            if (nonVisitorSpacesCount < props.maxSpaces) {
                props.setSelectedSpaces([...props.selectedSpaces, space]);
            }
        }
    };

    const isSelected = (space): boolean => {
        return props.selectedSpaces.findIndex(selectedSpace => selectedSpace._id === space._id) !== -1
    }

    const isDisabled = (space): boolean => {
        // Only count non-visitor spaces against the limit
        const nonVisitorSpacesCount = props.selectedSpaces.filter(s => !s.uploadedByVisitor).length;
        return nonVisitorSpacesCount >= props.maxSpaces && !isSelected(space)
    }

    return (
        <div className="grid grid-cols-1 gap-3 pb-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {selectedSpaceForDetails && <SpaceDetails space={selectedSpaceForDetails} onClose={() => setSelectedSpaceForDetails(undefined)}/>}

            {props.filteredSpaces.filter((space:Space) => !space.uploadedByVisitor).map((space: Space, index) => {
                const selected = isSelected(space);
                const disabled = isDisabled(space);

                return (
                    <div
                        key={index}
                        className={cn(
                            'relative overflow-hidden rounded-xl border bg-card transition-colors',
                            // Selection reads as an ink outline, the way it does everywhere else.
                            selected ? 'border-foreground' : 'hover:border-foreground/30',
                        )}
                    >
                        {/* Admin Actions — only render when the parent provided handlers */}
                        {(showAdminDelete || showAdminEdit || showOpenInManager) && (
                            <div className="absolute end-2 top-2 z-10 flex gap-1">
                                {showOpenInManager && (
                                    <OverlayAction
                                        onClick={() => props.onOpenInManager(space)}
                                        label="Open in Space Manager"
                                    >
                                        <Layers />
                                    </OverlayAction>
                                )}
                                {showAdminDelete && (
                                    <OverlayAction
                                        onClick={() => props.onDelete(space)}
                                        ariaLabel={t('COMMON.DELETE') as string}
                                        className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                    >
                                        <Trash2 />
                                    </OverlayAction>
                                )}

                                {showAdminEdit && (
                                    <OverlayAction
                                        onClick={() => props.handleEditSpace(space)}
                                        ariaLabel={t('COMMON.EDIT') as string}
                                    >
                                        <Pencil />
                                    </OverlayAction>
                                )}
                            </div>
                        )}

                        {/* Space Image and Icons */}
                        <div className="relative h-[200px] overflow-hidden bg-muted">
                            <img
                                src={space.thumbnailPath}
                                alt={space.name || space.spaceType}
                                className="block size-full object-cover"
                            />

                            {/* Action Icons (overlaid on image) — hidden in read-only mode */}
                            {!readOnly && (
                                <div className="absolute inset-x-0 bottom-2 flex items-center justify-center gap-2">
                                    <OverlayAction
                                        label={t('PAGES.MY_SPACES.VIEW_DETAILS') || 'View Details'}
                                        onClick={() => setSelectedSpaceForDetails(space)}
                                    >
                                        <Eye />
                                    </OverlayAction>

                                    <OverlayAction
                                        label={selected
                                            ? (t('COMMON.DESELECT') || 'Deselect')
                                            : (t('COMMON.SELECT') || 'Select')}
                                        onClick={() => !disabled && toggleSelect(space)}
                                        disabled={disabled}
                                        active={selected}
                                    >
                                        {selected ? <Check /> : <Circle />}
                                    </OverlayAction>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
